import axios from "axios";
import { toast } from "react-hot-toast";
import { useAuth } from "../components/features/AuthContext";

/**
 * Function to handle API requests with loading and error handling.
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} url - API endpoint URL
 * @param {object} [data] - Request body (for POST/PUT)
 * @returns {Promise<any>} - API response
 */

export const getHost = () => {
  const rawHost = localStorage.getItem("apiHost") || "http://localhost:8008/";
  return rawHost.endsWith("/") ? rawHost : `${rawHost}/`;
};

export const getUserId = () => {
  const data = localStorage.getItem("data");
  if (data) {
    try {
      const parsed = JSON.parse(data);
      return parsed.username || parsed.email || null;
    } catch {
      return null;
    }
  }
  return null;
};

export async function ApiRequest(method, endpoint, data = null, headers = null) {
  const toastId = toast.loading("Processing...");
  try {
    // Show loader/snackbar for request initiation
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    const rawHost = localStorage.getItem("apiHost") || "http://localhost:8008/";
    const host = rawHost.endsWith("/") ? rawHost : `${rawHost}/`;
    const cleanedEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    const url = host + cleanedEndpoint;

    // Get the actual JWT token from localStorage
    const token = localStorage.getItem("token");
    const authHeader = token ? `Bearer ${token}` : "";

    // Don't set Content-Type for FormData, let browser handle it
    const isFormData = data instanceof FormData;
    const commonHeaders = isFormData
      ? { "X-User-Id": getUserId(), "Authorization": authHeader }
      : { "Content-Type": "application/json", "X-User-Id": getUserId(), "Authorization": authHeader };

    const response = await axios({
      method,
      url,
      data,
      headers: headers == null ? commonHeaders : { ...headers, ...commonHeaders },
    });

    // Dismiss loader and show success message
    const successMsg = typeof response.data.message === 'string' ? response.data.message : "Success!";
    toast.success(successMsg, { id: toastId });
    return response.data;
  } catch (error) {
    // Handle API error and show snackbar
    let errorMessage = "Something went wrong";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        errorMessage = detail.map(d => (typeof d === 'object' ? `${d.field ? `${d.field}: ` : ''}${d.message || JSON.stringify(d)}` : String(d))).join(", ");
      } else if (typeof detail === 'object') {
        errorMessage = JSON.stringify(detail);
      } else {
        errorMessage = String(detail);
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    toast.error(errorMessage, { id: toastId });
    return error.response?.data || error.message || "Something went wrong";
  }
}

//downloadable api request

export async function ApiDownloadRequest(endpoint, params = {}) {
  const host = getHost();
  const url = `${host}${endpoint}`;

  try {
    const token = localStorage.getItem("token");
    const authHeader = token ? `Bearer ${token}` : "";

    const response = await axios.get(url, {
      params,
      headers: { "X-User-Id": getUserId(), "Authorization": authHeader },
      responseType: "blob", // Force Blob
    });

    // Extract filename if backend sends Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "downloaded_file";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) {
        fileName = match[1];
      }
    }

    // Create blob link & trigger download
    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);

    return { success: true, fileName };
  } catch (error) {
    console.error("File download failed", error);
    return { success: false, error };
  }
}



export async function ApiRequestNoLoder(method, endpoint, data = null, headers = null, agent=false) {
  try {
    // Show loader/snackbar for request initiation
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    const rawHost = agent ? "https://test.miiscollp.com/apis/" : localStorage.getItem("apiHost") || "http://localhost:8008/";
    const host = rawHost.endsWith("/") ? rawHost : `${rawHost}/`;
    const cleanedEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    const url = host + cleanedEndpoint;

    // Get the actual JWT token from localStorage
    const token = localStorage.getItem("token");
    const authHeader = token ? `Bearer ${token}` : "";

    const commonHeaders = { "Content-Type": "application/json", "X-User-Id": getUserId(), "Authorization": authHeader };

    const response = await axios({
      method,
      url,
      data,
      headers: headers == null ? commonHeaders : { ...commonHeaders, ...headers },
    });

    // Dismiss loader and show success message
    return response.data;
  } catch (error) {
    // Handle API error and show snackbar
    return error.response?.data || error.message || "Something went wrong";
  }
}
